<template>
  <!-- One file: the Download button the row always had (the row / card / list handles the click). -->
  <component
    :is="inline ? 'span' : 'button'"
    v-if="!multi"
    :type="inline ? undefined : 'button'"
    class="base-button-geom inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap bg-primary text-primary-foreground transition-colors hover:bg-primary-hover"
    :class="[rootClass, downloading ? 'opacity-70' : '']"
    data-variant="primary"
    :data-size="size"
    data-testid="notification-export-download"
    :aria-busy="downloading ? 'true' : undefined"
    @click="onSingleClick"
  >
    <i
      :class="downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'"
      aria-hidden="true"
    />
    {{ downloading ? 'Downloading…' : 'Download' }}
  </component>

  <!-- Several files: the dropdown — "Part N" one by one, or all at once. -->
  <component
    :is="inline ? 'span' : 'button'"
    v-else
    ref="triggerRef"
    :type="inline ? undefined : 'button'"
    :role="inline ? 'button' : undefined"
    :tabindex="inline ? 0 : undefined"
    class="base-button-geom inline-flex shrink-0 cursor-pointer items-center gap-1 whitespace-nowrap bg-primary text-primary-foreground transition-colors hover:bg-primary-hover"
    :class="[rootClass, downloading ? 'opacity-70' : '']"
    data-variant="primary"
    :data-size="size"
    data-testid="notification-export-download"
    aria-haspopup="menu"
    :aria-expanded="open ? 'true' : 'false'"
    :aria-busy="downloading ? 'true' : undefined"
    @click.stop.prevent="toggle"
    @keydown.enter.stop.prevent="toggle"
    @keydown.space.stop.prevent="toggle"
    @keydown.escape.stop="close"
  >
    <i
      :class="downloading ? 'ri-loader-4-line animate-spin' : 'ri-download-2-line'"
      aria-hidden="true"
    />
    {{ downloading ? 'Downloading…' : `Download (${parts.length})` }}
    <i
      class="ri-arrow-down-s-line text-[11px] transition-transform"
      :class="{ 'rotate-180': open }"
      aria-hidden="true"
    />
  </component>

  <Teleport to="body">
    <div
      v-if="multi && open"
      ref="menuRef"
      :class="menuClass"
      :style="menuStyle"
      role="menu"
      :aria-label="`Download ${exp.label} export`"
      data-testid="notification-export-menu"
      @click.stop
      @keydown.escape.stop="close"
    >
      <div class="flex flex-col gap-0.5 p-1.5">
        <button
          v-for="file in parts"
          :key="file.part"
          type="button"
          role="menuitem"
          :class="itemClass"
          :disabled="downloading"
          :data-testid="`notification-export-part-${file.part}`"
          @click.stop="pick(file.part)"
        >
          <span class="shrink-0 font-medium">Part {{ file.part }}</span>
          <span class="min-w-0 flex-1 truncate text-text-muted">{{ file.filename }}</span>
          <span
            v-if="formatPartSize(file.size_bytes)"
            class="shrink-0 tabular-nums text-text-muted"
          >
            {{ formatPartSize(file.size_bytes) }}
          </span>
        </button>
      </div>
      <div class="border-t border-[color:var(--sd-line)] p-1.5">
        <button
          type="button"
          role="menuitem"
          :class="[itemClass, 'justify-center font-medium']"
          :disabled="downloading"
          data-testid="notification-export-download-all"
          @click.stop="pickAll"
        >
          <i class="ri-download-2-line" aria-hidden="true" />
          Download all
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * NOTIF-011 C2 — the Download control of a ready export's row (the bell, the notifications page,
 * the toast card). One file: the Download button as before — the click is the row's / card's
 * (the parent downloads through the store). Several files: a dropdown in the Sales filter
 * controls' style (SdSalesDashboardPageFilterSelect, theme "sales"): "Part 1 … Part N" each a
 * download of its own, and "Download all" — the browser then asks once whether this site may
 * download several files. `inline` renders a span: the control sits inside the row's own
 * <button> in the bell and on the toast card, where a nested button is not allowed.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { NotificationItem } from '@/services/notificationsApi'
import {
  exportParts,
  formatPartSize,
  isMultiPart,
  type NotificationExport
} from '@/lib/notifications/exports'
import { useNotificationsStore } from '@/stores/notificationsStore'
import { dropdownOptionRowClass, getDropdownMenuPanelClass } from '@/utils/dropdownOptionRowClasses'
import { OVERLAY_Z_TOAST } from '@/lib/ui/overlayLayers'
import '@/styles/sales-dashboard-pages-theme.css'

const props = withDefaults(
  defineProps<{
    item: NotificationItem
    exp: NotificationExport
    /** The button's size: xs in the bell and on the card, small on the page. */
    size?: 'xs' | 'small'
    /** Render a span, not a button (the control sits inside the row's own button). */
    inline?: boolean
    /** Classes for the control itself (its place in the row). */
    rootClass?: string
  }>(),
  { size: 'xs', inline: false, rootClass: '' }
)

const emit = defineEmits<{
  /** One file: the click — the parent downloads (the store's open rule). */
  (e: 'open'): void
}>()

const store = useNotificationsStore()
const parts = computed(() => exportParts(props.exp))
const multi = computed(() => isMultiPart(props.exp))
const downloading = computed(() => store.isDownloading(props.exp.uuid))

const open = ref(false)
const triggerRef = ref<HTMLElement | null>(null)
const menuRef = ref<HTMLElement | null>(null)
const menuStyle = ref<Record<string, string>>({})

const menuClass = computed(
  () => `${getDropdownMenuPanelClass('sales')} notification-export-menu min-w-[240px] max-w-[360px]`
)
const itemClass = computed(() => dropdownOptionRowClass(false, false, 'sales'))

function onSingleClick(event: MouseEvent): void {
  if (props.inline) return // the row's own click handles it
  event.stopPropagation()
  if (downloading.value) return
  emit('open')
}

const MENU_GAP_PX = 4
const VIEWPORT_MARGIN_PX = 8

function place(): void {
  const trigger = triggerRef.value
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  const vw = window.innerWidth
  const vh = window.innerHeight
  const width = Math.min(360, Math.max(240, vw - 2 * VIEWPORT_MARGIN_PX))
  const left = Math.max(
    VIEWPORT_MARGIN_PX,
    Math.min(rect.right - width, vw - width - VIEWPORT_MARGIN_PX)
  )
  const spaceBelow = vh - rect.bottom - MENU_GAP_PX - VIEWPORT_MARGIN_PX
  const openAbove = spaceBelow < 160 && rect.top > spaceBelow
  // Above the toast layer too: the card's own Download opens this menu.
  const zIndex = String(OVERLAY_Z_TOAST + 10)
  menuStyle.value = openAbove
    ? {
        position: 'fixed',
        bottom: `${vh - rect.top + MENU_GAP_PX}px`,
        left: `${left}px`,
        width: `${width}px`,
        zIndex
      }
    : {
        position: 'fixed',
        top: `${rect.bottom + MENU_GAP_PX}px`,
        left: `${left}px`,
        width: `${width}px`,
        zIndex
      }
}

async function toggle(): Promise<void> {
  if (downloading.value) return
  open.value = !open.value
  if (open.value) {
    await nextTick()
    place()
  }
}

function close(): void {
  open.value = false
}

function pick(part: number): void {
  close()
  void store.downloadExportPart(props.item, props.exp, part)
}

function pickAll(): void {
  close()
  void store.downloadExportAll(props.item, props.exp)
}

function onDocumentClick(event: MouseEvent): void {
  if (!open.value) return
  const target = event.target as Node | null
  if (target && (triggerRef.value?.contains(target) || menuRef.value?.contains(target))) return
  close()
}

function onScrollOrResize(): void {
  if (open.value) place()
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick, true)
  window.addEventListener('scroll', onScrollOrResize, true)
  window.addEventListener('resize', onScrollOrResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onDocumentClick, true)
  window.removeEventListener('scroll', onScrollOrResize, true)
  window.removeEventListener('resize', onScrollOrResize)
})

// The export may turn expired under an open menu (the poll rewrote the row): nothing to pick.
watch(
  () => props.exp.status,
  (status) => {
    if (status !== 'ready') close()
  }
)
</script>
